import React, { useEffect, useMemo, useState } from 'react';
import ToggleSwitch from './ToggleSwitch';
import InputField from './InputField';
import DataTable from './DataTable';
import {
  deletePopupSubscriber,
  getAdminPopupConfig,
  getAdminPopupEmailTemplate,
  getPopupSubscribers,
  resendPopupSubscriberEmail,
  updateAdminPopupEmailTemplate,
  updateAdminPopupConfig,
  uploadAdminPopupThumbnail,
} from '../../../services/popup';
import { fetchPromotions } from '../../../services/promotion';

export interface PromotionPopupConfig {
  isActive: boolean;
  title: string;
  description: string;
  buttonText: string;
  promotionId?: string;
  thumbnailUrl?: string;
}

export interface Subscriber {
  id: string;
  email: string;
  promotionId?: string | null;
  subscribedAt: string;
}

export interface PopupEmailTemplateConfig {
  isActive: boolean;
  subject: string;
  message: string;
  link: string;
  promotionId: string;
}

interface PromotionOption {
  id: string;
  label: string;
}

const RECOMMENDED_IMAGE_WIDTH = 1200;
const RECOMMENDED_IMAGE_HEIGHT = 675;
const MAX_IMAGE_SIZE_MB = 2;

const initialConfig: PromotionPopupConfig = {
  isActive: false,
  title: 'Únete a nuestra comunidad',
  description: 'Recibe novedades, promociones y contenido especial directamente en tu correo.',
  buttonText: 'Suscribirme',
  promotionId: '',
  thumbnailUrl: '',
};

const initialEmailTemplate: PopupEmailTemplateConfig = {
  isActive: false,
  subject: '',
  message: '',
  link: '',
  promotionId: '',
};

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleString('es-MX', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
};

const resolveActiveFlag = (payload: any): boolean => {
  if (typeof payload?.isActive === 'boolean') {
    return payload.isActive;
  }

  if (typeof payload?.active === 'boolean') {
    return payload.active;
  }

  return false;
};

const PromotionPopupSettings: React.FC = () => {
  const [config, setConfig] = useState<PromotionPopupConfig>(initialConfig);
  const [emailTemplate, setEmailTemplate] = useState<PopupEmailTemplateConfig>(initialEmailTemplate);
  const [promotions, setPromotions] = useState<PromotionOption[]>([]);
  const [subscribers, setSubscribers] = useState<Subscriber[]>([]);
  const [page, setPage] = useState(0);
  const [size] = useState(20);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isLoadingSubscribers, setIsLoadingSubscribers] = useState(false);
  const [imageError, setImageError] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [isInitializing, setIsInitializing] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [statusMessage, setStatusMessage] = useState('');
  const [templateStatusMessage, setTemplateStatusMessage] = useState('');
  const [subscriberActionMessage, setSubscriberActionMessage] = useState('');
  const [removingSubscriberId, setRemovingSubscriberId] = useState<string | null>(null);
  const [resendingSubscriberId, setResendingSubscriberId] = useState<string | null>(null);

  const canEditConfig = !isSaving;
  const canEditTemplate = !isSavingTemplate;

  const subscriberRows = useMemo(() => subscribers, [subscribers]);

  const handleConfigChange = (field: keyof PromotionPopupConfig, value: string | boolean) => {
    setConfig((prev) => ({
      ...prev,
      [field]: value,
    }));
    setStatusMessage('');
    setSubscriberActionMessage('');
    setErrorMessage('');
  };

  const handleEmailTemplateChange = (field: keyof PopupEmailTemplateConfig, value: string | boolean) => {
    setEmailTemplate((prev) => ({
      ...prev,
      [field]: value,
    }));
    setTemplateStatusMessage('');
    setSubscriberActionMessage('');
    setErrorMessage('');
  };

  const handleImageUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    if (!file.type.startsWith('image/')) {
      setImageError('Selecciona un archivo de imagen válido.');
      return;
    }

    const maxBytes = MAX_IMAGE_SIZE_MB * 1024 * 1024;
    if (file.size > maxBytes) {
      setImageError(`La imagen no puede exceder ${MAX_IMAGE_SIZE_MB}MB.`);
      return;
    }

    setIsSaving(true);
    setImageError('');
    setErrorMessage('');
    setStatusMessage('');

    uploadAdminPopupThumbnail(file)
      .then((updatedConfig) => {
        setConfig(updatedConfig);
        setStatusMessage('Imagen actualizada correctamente.');
      })
      .catch((error: Error) => {
        setImageError(error.message);
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const loadPopupConfig = async () => {
    const response = await getAdminPopupConfig();
    setConfig({
      isActive: Boolean(response?.isActive),
      title: response?.title || '',
      description: response?.description || '',
      buttonText: response?.buttonText || '',
      promotionId: response?.promotionId || '',
      thumbnailUrl: response?.thumbnailUrl || '',
    });
  };

  const loadEmailTemplateConfig = async () => {
    const response = await getAdminPopupEmailTemplate();
    setEmailTemplate({
      isActive: resolveActiveFlag(response),
      subject: response?.subject || '',
      message: response?.message || '',
      link: response?.link || '',
      promotionId: response?.promotionId || '',
    });
  };

  const loadPromotions = async () => {
    try {
      const response = (await fetchPromotions()) as unknown;
      const paginatedResponse = response as { content?: any[] } | null;
      const source = Array.isArray(response)
        ? response
        : paginatedResponse && Array.isArray(paginatedResponse.content)
          ? paginatedResponse.content
          : [];

      const options: PromotionOption[] = source
        .map((item: any) => {
          const id = String(item?.id || item?._id || item?.promotionId || '').trim();
          if (!id) {
            return null;
          }

          const label = String(item?.title || item?.name || item?.subtitle || id).trim();
          return {
            id,
            label,
          };
        })
        .filter(Boolean) as PromotionOption[];

      setPromotions(options);
    } catch (error) {
      setPromotions([]);
    }
  };

  const loadSubscribers = async (nextPage: number) => {
    setIsLoadingSubscribers(true);
    setErrorMessage('');

    try {
      const response = await getPopupSubscribers({ page: nextPage, size, sort: 'subscribedAt,desc' });
      setSubscribers(Array.isArray(response?.content) ? response.content : []);
      setPage(typeof response?.number === 'number' ? response.number : nextPage);
      setTotalPages(typeof response?.totalPages === 'number' ? response.totalPages : 0);
      setTotalElements(typeof response?.totalElements === 'number' ? response.totalElements : 0);
    } catch (error: any) {
      setErrorMessage(error?.message || 'No fue posible cargar suscriptoras.');
    } finally {
      setIsLoadingSubscribers(false);
    }
  };

  const handleRemoveSubscriber = async (subscriber: Subscriber) => {
    const isConfirmed = window.confirm(`¿Eliminar la suscripción de ${subscriber.email}?`);
    if (!isConfirmed) {
      return;
    }

    setRemovingSubscriberId(subscriber.id);
    setSubscriberActionMessage('');
    setErrorMessage('');

    try {
      await deletePopupSubscriber(subscriber.id);

      const hasOnlyOneInPage = subscribers.length === 1;
      const targetPage = hasOnlyOneInPage && page > 0 ? page - 1 : page;
      await loadSubscribers(targetPage);

      setSubscriberActionMessage('Suscriptora eliminada correctamente.');
    } catch (error: any) {
      setErrorMessage(error?.message || 'No fue posible eliminar la suscripción.');
    } finally {
      setRemovingSubscriberId(null);
    }
  };

  const handleResendSubscriberEmail = async (subscriber: Subscriber) => {
    setResendingSubscriberId(subscriber.id);
    setSubscriberActionMessage('');
    setErrorMessage('');

    try {
      await resendPopupSubscriberEmail(subscriber.id);
      setSubscriberActionMessage(`Correo reenviado a ${subscriber.email}.`);
    } catch (error: any) {
      setErrorMessage(error?.message || 'No fue posible reenviar el correo promocional.');
    } finally {
      setResendingSubscriberId(null);
    }
  };

  useEffect(() => {
    const initialize = async () => {
      setIsInitializing(true);
      setErrorMessage('');

      try {
        await Promise.all([
          loadPopupConfig(),
          loadEmailTemplateConfig(),
          loadPromotions(),
          loadSubscribers(0),
        ]);
      } catch (error: any) {
        setErrorMessage(error?.message || 'No fue posible inicializar la configuración del popup.');
      } finally {
        setIsInitializing(false);
      }
    };

    initialize();
  }, []);

  const handleSaveConfig = async () => {
    const nextTitle = String(config.title || '').trim();
    const nextDescription = String(config.description || '').trim();
    const nextButtonText = String(config.buttonText || '').trim();
    const nextPromotionId = String(config.promotionId || '').trim();

    if (!nextTitle) {
      setErrorMessage('El título es obligatorio.');
      return;
    }

    if (nextTitle.length > 120) {
      setErrorMessage('El título no puede superar 120 caracteres.');
      return;
    }

    if (!nextDescription) {
      setErrorMessage('La descripción es obligatoria.');
      return;
    }

    if (nextDescription.length > 500) {
      setErrorMessage('La descripción no puede superar 500 caracteres.');
      return;
    }

    if (!nextButtonText) {
      setErrorMessage('El texto del botón es obligatorio.');
      return;
    }

    if (nextButtonText.length > 80) {
      setErrorMessage('El texto del botón no puede superar 80 caracteres.');
      return;
    }

    setIsSaving(true);
    setStatusMessage('');
    setErrorMessage('');

    try {
      if (nextPromotionId && promotions.length > 0) {
        const promotionExists = promotions.some((promotion) => promotion.id === nextPromotionId);
        if (!promotionExists) {
          setErrorMessage('El Promotion ID del popup no existe en promociones.');
          setIsSaving(false);
          return;
        }
      }

      const updated = await updateAdminPopupConfig({
        isActive: Boolean(config.isActive),
        title: nextTitle,
        description: nextDescription,
        buttonText: nextButtonText,
        promotionId: nextPromotionId || null,
        thumbnailUrl: config.thumbnailUrl || '',
      });

      setConfig({
        isActive: Boolean(updated?.isActive),
        title: updated?.title || nextTitle,
        description: updated?.description || nextDescription,
        buttonText: updated?.buttonText || nextButtonText,
        promotionId: updated?.promotionId || '',
        thumbnailUrl: updated?.thumbnailUrl || config.thumbnailUrl || '',
      });
      setStatusMessage(`Popup ${Boolean(updated?.isActive ?? config.isActive) ? 'activo' : 'inactivo'} guardado correctamente.`);
    } catch (error: any) {
      setErrorMessage(error?.message || 'No fue posible guardar la configuración.');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveEmailTemplate = async () => {
    const nextSubject = String(emailTemplate.subject || '').trim();
    const nextMessage = String(emailTemplate.message || '').trim();
    const nextLink = String(emailTemplate.link || '').trim();
    const nextPromotionId = String(emailTemplate.promotionId || '').trim();

    if (emailTemplate.isActive && !nextSubject) {
      setErrorMessage('El asunto del correo es obligatorio cuando la plantilla está activa.');
      return;
    }

    if (emailTemplate.isActive && !nextMessage) {
      setErrorMessage('El mensaje del correo es obligatorio cuando la plantilla está activa.');
      return;
    }

    if (nextPromotionId && promotions.length > 0) {
      const promotionExists = promotions.some((promotion) => promotion.id === nextPromotionId);
      if (!promotionExists) {
        setErrorMessage('El Promotion ID de la plantilla no existe en promociones.');
        return;
      }
    }

    setIsSavingTemplate(true);
    setTemplateStatusMessage('');
    setErrorMessage('');

    try {
      const updated = await updateAdminPopupEmailTemplate({
        isActive: Boolean(emailTemplate.isActive),
        active: Boolean(emailTemplate.isActive),
        subject: nextSubject || null,
        message: nextMessage || null,
        link: nextLink || null,
        promotionId: nextPromotionId || null,
      });

      setEmailTemplate({
        isActive: resolveActiveFlag(updated),
        subject: updated?.subject || '',
        message: updated?.message || '',
        link: updated?.link || '',
        promotionId: updated?.promotionId || '',
      });
      setTemplateStatusMessage(`Plantilla ${resolveActiveFlag(updated) ? 'activa' : 'inactiva'} guardada correctamente.`);
    } catch (error: any) {
      setErrorMessage(error?.message || 'No fue posible guardar la plantilla de correo.');
    } finally {
      setIsSavingTemplate(false);
    }
  };

  if (isInitializing) {
    return (
      <div className="flex items-center justify-center py-10">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-slate-300 border-t-slate-700" />
        <span className="ml-3 text-sm text-slate-600">Cargando configuración...</span>
      </div>
    );
  }

  return (
    <section className="w-full space-y-6">
      <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <h2 className="text-lg font-semibold text-slate-900">Promotion Popup</h2>
        <p className="mt-1 text-sm text-slate-600">
          Configura el popup real del frontend y administra los correos recolectados.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${config.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>
            Popup: {config.isActive ? 'Activo' : 'Inactivo'}
          </span>
          <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">
            Popup Promotion ID: {config.promotionId || 'Global'}
          </span>
          <span className={`rounded-full px-3 py-1 text-xs font-semibold ${emailTemplate.isActive ? 'bg-emerald-100 text-emerald-700' : 'bg-slate-200 text-slate-700'}`}>
            Email: {emailTemplate.isActive ? 'Activo' : 'Inactivo'}
          </span>
          <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
            Promotion ID: {emailTemplate.promotionId || 'Global'}
          </span>
        </div>
        <p className="mt-3 text-xs text-slate-500">
          Regla de envío: el email se envía solo cuando la plantilla está activa y, si tiene Promotion ID, debe coincidir con el Promotion ID enviado al suscribir.
        </p>
        {errorMessage ? <p className="mt-3 text-sm font-medium text-red-600">{errorMessage}</p> : null}
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
          <ToggleSwitch
            label="Popup activo"
            checked={config.isActive}
            onChange={(checked) => handleConfigChange('isActive', checked)}
            helperText={
              config.isActive
                ? 'El popup está activo y visible en frontend.'
                : 'El popup está inactivo y oculto en frontend.'
            }
          />

          <InputField
            id="popup-title"
            label="Title"
            value={config.title}
            onChange={(value) => handleConfigChange('title', value)}
            placeholder="Escribe el título"
            disabled={!canEditConfig}
          />

          <InputField
            id="popup-description"
            label="Description"
            value={config.description}
            onChange={(value) => handleConfigChange('description', value)}
            placeholder="Escribe la descripción"
            disabled={!canEditConfig}
            multiline
          />

          <InputField
            id="popup-button-text"
            label="Button Text"
            value={config.buttonText}
            onChange={(value) => handleConfigChange('buttonText', value)}
            placeholder="Ej. Suscribirme"
            disabled={!canEditConfig}
          />

          <label htmlFor="popup-config-promotion" className="block">
            <span className="text-sm font-medium text-slate-700">Promotion ID del popup (opcional)</span>
            <input
              id="popup-config-promotion"
              list="popup-config-promotion-options"
              type="text"
              value={config.promotionId || ''}
              onChange={(event) => handleConfigChange('promotionId', event.target.value)}
              placeholder="Ej. promo123"
              disabled={!canEditConfig}
              className={`mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-slate-500 ${
                !canEditConfig ? 'bg-slate-100 text-slate-500' : 'bg-white text-slate-900'
              }`}
            />
            <datalist id="popup-config-promotion-options">
              {promotions.map((promotion) => (
                <option key={promotion.id} value={promotion.id}>
                  {promotion.label}
                </option>
              ))}
            </datalist>
            <span className="mt-1 block text-xs text-slate-500">
              Este Promotion ID se enviará al suscribir desde el popup cuando no venga uno por URL.
            </span>
          </label>

          <div className="rounded-xl border border-slate-200 p-4">
            <label htmlFor="popup-image" className="block text-sm font-medium text-slate-700">
              Imagen del popup
            </label>
            <p className="mt-1 text-xs text-slate-500">
              Recomendado: {RECOMMENDED_IMAGE_WIDTH}x{RECOMMENDED_IMAGE_HEIGHT}px (relación 16:9), máximo {MAX_IMAGE_SIZE_MB}MB.
            </p>
            <input
              id="popup-image"
              type="file"
              accept="image/*"
              onChange={handleImageUpload}
              disabled={!canEditConfig}
              className="mt-3 block w-full text-sm text-slate-700 file:mr-3 file:rounded-md file:border-0 file:bg-slate-100 file:px-3 file:py-2 file:text-xs file:font-semibold file:text-slate-700 disabled:cursor-not-allowed"
            />
            {imageError ? <span className="mt-1 block text-xs text-red-600">{imageError}</span> : null}
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <button
              type="button"
              disabled={isSaving}
              onClick={handleSaveConfig}
              className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
            >
              {isSaving ? 'Guardando...' : 'Guardar configuración'}
            </button>
            {statusMessage ? <span className="text-sm text-emerald-600">{statusMessage}</span> : null}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
          <h3 className="text-sm font-semibold uppercase tracking-wide text-slate-600">Vista previa frontend</h3>
          {config.isActive ? (
            <div className="mt-3 rounded-lg border border-slate-200 bg-slate-50 p-4">
              <div
                className="overflow-hidden rounded-md border border-slate-200 bg-white"
                style={{ aspectRatio: '16 / 9' }}
              >
                {config.thumbnailUrl ? (
                  <img
                    src={config.thumbnailUrl}
                    alt="Promo"
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-xs text-slate-500">
                    Sin imagen (recomendado {RECOMMENDED_IMAGE_WIDTH}x{RECOMMENDED_IMAGE_HEIGHT}px)
                  </div>
                )}
              </div>
              <p className="text-base font-semibold text-slate-900">{config.title}</p>
              <p className="mt-2 text-sm text-slate-700">{config.description}</p>
              <button type="button" className="mt-4 rounded-md bg-slate-900 px-3 py-2 text-xs font-semibold text-white">
                {config.buttonText}
              </button>
            </div>
          ) : (
            <div className="mt-3 rounded-lg border border-dashed border-slate-300 p-4 text-sm text-slate-500">
              Popup oculto: activa el switch para mostrarlo en frontend (simulación).
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <h3 className="text-base font-semibold text-slate-900">Plantilla de email de suscripción</h3>

        <ToggleSwitch
          label="Plantilla de correo activa"
          checked={emailTemplate.isActive}
          onChange={(checked) => handleEmailTemplateChange('isActive', checked)}
          helperText={
            emailTemplate.isActive
              ? 'Se enviará correo automáticamente según la regla de promotionId.'
              : 'No se enviarán correos automáticos desde el popup.'
          }
        />

        <InputField
          id="popup-template-subject"
          label="Asunto"
          value={emailTemplate.subject}
          onChange={(value) => handleEmailTemplateChange('subject', value)}
          placeholder="Ej. Bienvenida a Sarasanas"
          disabled={!canEditTemplate}
          required={emailTemplate.isActive}
        />

        <InputField
          id="popup-template-message"
          label="Mensaje"
          value={emailTemplate.message}
          onChange={(value) => handleEmailTemplateChange('message', value)}
          placeholder="Mensaje que se enviará al suscriptor"
          disabled={!canEditTemplate}
          required={emailTemplate.isActive}
          multiline
        />

        <InputField
          id="popup-template-link"
          label="Link (opcional)"
          value={emailTemplate.link}
          onChange={(value) => handleEmailTemplateChange('link', value)}
          placeholder="https://sarasanas.com/promocion-especial"
          disabled={!canEditTemplate}
        />

        <label htmlFor="popup-template-promotion" className="block">
          <span className="text-sm font-medium text-slate-700">Promotion ID (opcional)</span>
          <input
            id="popup-template-promotion"
            list="popup-template-promotion-options"
            type="text"
            value={emailTemplate.promotionId}
            onChange={(event) => handleEmailTemplateChange('promotionId', event.target.value)}
            disabled={!canEditTemplate}
            placeholder="Ej. promo123 o vacío para plantilla global"
            className={`mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm outline-none transition focus:border-slate-500 ${
              !canEditTemplate ? 'bg-slate-100 text-slate-500' : 'bg-white text-slate-900'
            }`}
          />
          <datalist id="popup-template-promotion-options">
            {promotions.map((promotion) => (
              <option key={promotion.id} value={promotion.id}>
                {promotion.label}
              </option>
            ))}
          </datalist>
          <div className="mt-2 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={!canEditTemplate}
              onClick={() => handleEmailTemplateChange('promotionId', '')}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Usar plantilla global
            </button>
            <button
              type="button"
              disabled={!canEditTemplate || promotions.length === 0}
              onClick={() => handleEmailTemplateChange('promotionId', promotions[0]?.id || '')}
              className="rounded-md border border-slate-300 bg-white px-2 py-1 text-xs font-medium text-slate-700 hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              Usar primer promotionId disponible
            </button>
          </div>
          <span className="mt-1 block text-xs text-slate-500">
            Si eliges un promotionId, el correo solo se enviará cuando el subscribe use ese mismo promotionId.
          </span>
        </label>

        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={isSavingTemplate}
            onClick={handleSaveEmailTemplate}
            className="rounded-lg bg-slate-900 px-4 py-2 text-sm font-medium text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-400"
          >
            {isSavingTemplate ? 'Guardando plantilla...' : 'Guardar plantilla email'}
          </button>
          {templateStatusMessage ? <span className="text-sm text-emerald-600">{templateStatusMessage}</span> : null}
        </div>
      </div>

      <div className="space-y-4 rounded-xl border border-slate-200 bg-white p-4 sm:p-5">
        <h3 className="text-base font-semibold text-slate-900">Email Collection</h3>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="text-sm text-slate-600">Total suscriptoras: <span className="font-semibold">{totalElements}</span></p>
          <button
            type="button"
            onClick={() => loadSubscribers(page)}
            disabled={isLoadingSubscribers}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {isLoadingSubscribers ? 'Actualizando...' : 'Actualizar lista'}
          </button>
        </div>
        {subscriberActionMessage ? <p className="text-sm font-medium text-emerald-600">{subscriberActionMessage}</p> : null}

        <DataTable<Subscriber>
          title={`Collected Emails (${subscriberRows.length})`}
          rows={subscriberRows}
          columns={[
            {
              key: 'email',
              header: 'Email',
              render: (row) => row.email,
            },
            {
              key: 'subscribedAt',
              header: 'Date subscribed',
              render: (row) => formatDate(row.subscribedAt),
            },
            {
              key: 'promotionId',
              header: 'Promotion ID',
              render: (row) => row.promotionId || 'Global',
            },
            {
              key: 'actions',
              header: 'Acciones',
              render: (row) => (
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={Boolean(removingSubscriberId) || Boolean(resendingSubscriberId) || isLoadingSubscribers}
                    onClick={() => handleResendSubscriberEmail(row)}
                    className="rounded-md border border-sky-300 bg-white px-2 py-1 text-xs font-semibold text-sky-700 transition hover:border-sky-500 hover:text-sky-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {resendingSubscriberId === row.id ? 'Reenviando...' : 'Reenviar email'}
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(removingSubscriberId) || Boolean(resendingSubscriberId) || isLoadingSubscribers}
                    onClick={() => handleRemoveSubscriber(row)}
                    className="rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-semibold text-red-700 transition hover:border-red-500 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    {removingSubscriberId === row.id ? 'Eliminando...' : 'Eliminar'}
                  </button>
                </div>
              ),
            },
          ]}
          emptyMessage="Aún no hay emails suscritos."
        />

        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => loadSubscribers(page - 1)}
            disabled={isLoadingSubscribers || page <= 0}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Anterior
          </button>
          <span className="text-sm text-slate-600">
            Página {totalPages === 0 ? 0 : page + 1} de {totalPages}
          </span>
          <button
            type="button"
            onClick={() => loadSubscribers(page + 1)}
            disabled={isLoadingSubscribers || page >= totalPages - 1 || totalPages === 0}
            className="rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition hover:border-slate-500 disabled:cursor-not-allowed disabled:opacity-60"
          >
            Siguiente
          </button>
        </div>
      </div>
    </section>
  );
};

export default PromotionPopupSettings;
